import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import METHOD from '../_methods';

const endpoint = 'api/v1/pocketsafe-administration/customers/';

type CustomerListParams = {
  limit: number;
  offset: number;
  ordering?: string;
  search?: string;
  status?: string;
  payment_method?: string;
  onboarding_type?: string;
};

type CustomerExportParams = {
  search?: string;
  status?: string;
  payment_method?: string;
  onboarding_type?: string;
};

const downloadCsv = async (params: CustomerExportParams & { exportType: 'csv' | 'invoice_csv'; filename: string }) => {
  const searchParams = new URLSearchParams();
  searchParams.set('export', params.exportType);
  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.payment_method) searchParams.set('payment_method', params.payment_method);
  if (params.onboarding_type) searchParams.set('onboarding_type', params.onboarding_type);

  const response = await axios.get(`${endpoint}?${searchParams.toString()}`, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', params.filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
  return true;
};

type CustomerOnboardPayload = {
  organization: {
    name: string;
    organization_number: string;
    country: string;
    business_type: string;
    organization_size: string;
    force_duplicate_name: boolean;
  };
  owner: {
    first_name: string;
    last_name: string;
    email: string;
    language_code: string;
  };
  administrator: {
    first_name: string;
    last_name: string;
    email: string;
    language_code: string;
  } | null;
};

const pocketsafeCustomersApi = {
  getAll: ({ limit, offset, ordering, search, status, payment_method, onboarding_type }: CustomerListParams) => ({
    url: createUrlWithParams(endpoint, {
      limit,
      offset,
      ...(ordering ? { ordering } : {}),
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(payment_method ? { payment_method } : {}),
      ...(onboarding_type ? { onboarding_type } : {}),
    }),
    method: METHOD.GET,
  }),
  onboard: async (inputData: CustomerOnboardPayload) => {
    const { data } = await axios.post(`${endpoint}onboard/`, inputData);
    return data;
  },
  exportCsv: async (params: CustomerExportParams) => downloadCsv({ ...params, exportType: 'csv', filename: 'customers.csv' }),
  exportInvoiceCsv: async (params: CustomerExportParams) =>
    downloadCsv({ ...params, exportType: 'invoice_csv', filename: 'customer-invoices.csv' }),
  getLanguages: async () => {
    const { data } = await axios.get('api/v1/common/languages');
    return data;
  },
  getBusinessTypes: async () => {
    const { data } = await axios.get('api/v1/business_types/');
    return data;
  },
  getOrganizationSizes: async () => {
    const { data } = await axios.get('api/v1/organization_sizes/');
    return data;
  },
};

export default pocketsafeCustomersApi;
